import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { card, input, label, btnPrimary, btnGhost, h1 } from "./ui";
import { Plus, Sparkles, Pencil, Trash2, Loader2, ExternalLink, Globe, FileText, Eye, EyeOff } from "lucide-react";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

export default function Seo() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [topic, setTopic] = useState("");
  const [aiType, setAiType] = useState("pagina");
  const [generating, setGenerating] = useState(false);

  const load = () => api.get("/contents").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const toggleStatus = async (c) => {
    const next = c.status === "publicado" ? "rascunho" : "publicado";
    try {
      await api.post(`/contents/${c.id}/status`, { status: next });
      toast.success(next === "publicado" ? "Conteúdo publicado — já está no ar e no sitemap." : "Conteúdo despublicado.");
      load();
    } catch { toast.error("Não consegui alterar o status."); }
  };

  const del = async (c) => {
    if (!window.confirm(`Excluir "${c.title}" definitivamente?`)) return;
    try {
      await api.delete(`/contents/${c.id}`);
      toast.success("Conteúdo excluído.");
      load();
    } catch { toast.error("Não consegui excluir."); }
  };

  const generate = async (e) => {
    e.preventDefault();
    if (topic.trim().length < 3) return toast.error("Descreva o tema do conteúdo.");
    setGenerating(true);
    try {
      const { data } = await api.post("/contents/generate", { topic: topic.trim(), type: aiType }, { timeout: 120000 });
      toast.success("Rascunho gerado! Revise e edite antes de publicar.");
      setAiOpen(false);
      setTopic("");
      navigate("/dashboard/seo/novo", { state: { draft: data } });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const publicUrl = (c) => (c.type === "artigo" ? `/conteudos/${c.slug}` : `/${c.slug}`);

  return (
    <div data-testid="seo-page" className="max-w-4xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Captação orgânica</p>
          <h1 className={h1}>SEO e Conteúdo</h1>
          <p className="font-body text-sm text-[#7A8A7A] mt-2 max-w-xl">
            Crie páginas e artigos otimizados para o Google sobre atendimento psicológico online.
            A IA gera rascunhos — você revisa e publica quando estiver pronto.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setAiOpen(true)} data-testid="seo-create-ai" className={`${btnPrimary} inline-flex items-center gap-2`}>
            <Sparkles className="w-4 h-4" /> Criar com IA
          </button>
          <button onClick={() => navigate("/dashboard/seo/novo")} data-testid="seo-create-manual" className={`${btnGhost} inline-flex items-center gap-2`}>
            <Plus className="w-4 h-4" /> Novo manual
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-3">
        {items === null ? (
          <Loader2 className="w-6 h-6 animate-spin text-[#5B6E5B]" />
        ) : items.length === 0 ? (
          <div className={`${card} p-10 text-center`} data-testid="seo-empty">
            <FileText className="w-8 h-8 text-[#5B6E5B] mx-auto mb-3" />
            <p className="font-body text-sm text-[#7A8A7A]">Nenhum conteúdo ainda. Comece com "Criar com IA" — informe um tema como "Terapia online para ansiedade" e revise o rascunho gerado.</p>
          </div>
        ) : (
          items.map((c) => (
            <div key={c.id} className={`${card} p-4 sm:p-5 flex items-center gap-4 flex-wrap`} data-testid="seo-row">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-body ${c.type === "artigo" ? "bg-[#5B6E5B]/12 text-[#4A5D4E]" : "bg-[#C87A5E]/15 text-[#B2644D]"}`}>
                    {c.type === "artigo" ? <FileText className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                    {c.type === "artigo" ? "Artigo" : "Página SEO"}
                  </span>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-body ${c.status === "publicado" ? "bg-[#5B6E5B] text-[#FDFBF7]" : "bg-[#F5F0E6] text-[#7A8A7A]"}`} data-testid="seo-status-badge">
                    {c.status === "publicado" ? "Publicado" : "Rascunho"}
                  </span>
                </div>
                <p className="font-display text-lg text-[#2C332A] mt-1.5">{c.title}</p>
                <p className="font-body text-xs text-[#7A8A7A]">{publicUrl(c)} · atualizado em {fmtDateTime(c.updated_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                {c.status === "publicado" && (
                  <a href={publicUrl(c)} target="_blank" rel="noreferrer" data-testid="seo-view-public" title="Ver página pública"
                    className="grid place-items-center w-9 h-9 rounded-full border border-[#5B6E5B]/25 text-[#5B6E5B] hover:bg-white transition-colors">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button onClick={() => navigate(`/dashboard/seo/${c.id}`)} data-testid="seo-edit" title="Editar"
                  className="grid place-items-center w-9 h-9 rounded-full border border-[#5B6E5B]/25 text-[#5B6E5B] hover:bg-white transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => toggleStatus(c)} data-testid="seo-toggle-status"
                  title={c.status === "publicado" ? "Despublicar" : "Publicar"}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs transition-colors ${c.status === "publicado" ? "border border-[#5B6E5B]/25 text-[#5B6E5B] hover:bg-white" : "bg-[#5B6E5B] text-[#FDFBF7] hover:bg-[#4A5D4E]"}`}>
                  {c.status === "publicado" ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {c.status === "publicado" ? "Despublicar" : "Publicar"}
                </button>
                <button onClick={() => del(c)} data-testid="seo-delete" title="Excluir"
                  className="grid place-items-center w-9 h-9 rounded-full text-[#C87A5E] hover:bg-[#C87A5E]/10 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="bg-[#FDFBF7]" data-testid="seo-ai-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#2C332A]">Criar conteúdo com IA</DialogTitle>
            <DialogDescription className="sr-only">Informe o tema e o tipo para a IA gerar um rascunho otimizado para SEO</DialogDescription>
          </DialogHeader>
          <form onSubmit={generate} className="space-y-4">
            <div>
              <label className={label}>Tema ou objetivo do conteúdo</label>
              <input className={input} value={topic} onChange={(e) => setTopic(e.target.value)}
                placeholder="Ex.: Terapia online para ansiedade" data-testid="seo-ai-topic" required />
            </div>
            <div>
              <label className={label}>Tipo de conteúdo</label>
              <select className={input} value={aiType} onChange={(e) => setAiType(e.target.value)} data-testid="seo-ai-type">
                <option value="pagina">Página SEO (serviço/intenção de busca)</option>
                <option value="artigo">Artigo educativo</option>
              </select>
            </div>
            <p className="font-body text-xs text-[#7A8A7A] leading-relaxed">
              A IA gera título, texto com subtítulos, meta title, meta description, perguntas frequentes e uma chamada para agendamento —
              sempre como <strong>rascunho</strong> para você revisar. Linguagem profissional, sem promessas de resultado.
            </p>
            <div className="flex justify-end gap-3">
              <button type="button" className={btnGhost} onClick={() => setAiOpen(false)}>Cancelar</button>
              <button type="submit" className={btnPrimary} disabled={generating} data-testid="seo-ai-generate">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Gerar rascunho
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
